/**
 * Utility functions for copying tables in different formats
 */

/**
 * Copy table as rich text (HTML) with CSV fallback
 * This format works best for pasting into Google Sheets, Excel, Google Docs
 */
export const copyTableAsRichText = async (tableElement) => {
  try {
    const html = tableElement.outerHTML;
    const csvFallback = tableToCsv(tableElement);

    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([csvFallback], { type: 'text/plain' })
      })
    ]);

    return true;
  } catch (error) {
    console.error('Failed to copy as rich text:', error);
    // Fallback to plain text CSV
    return copyTableAsCsv(tableElement);
  }
};

/**
 * Copy table as Markdown format
 * Useful for pasting into markdown editors
 */
export const copyTableAsMarkdown = async (tableElement) => {
  try {
    const rows = [];
    
    // Extract all rows (both thead and tbody)
    tableElement.querySelectorAll('tr').forEach((row, rowIndex) => {
      const cells = [];
      row.querySelectorAll('th, td').forEach(cell => {
        // Escape pipes and preserve text content
        const text = cell.textContent.trim().replace(/\|/g, '\\|');
        cells.push(text);
      });
      rows.push(cells);
    });

    if (rows.length === 0) {
      throw new Error('No rows found in table');
    }

    // Build markdown table
    const markdown = [
      '| ' + rows[0].join(' | ') + ' |',
      '| ' + rows[0].map(() => '---').join(' | ') + ' |',
      ...rows.slice(1).map(row => '| ' + row.join(' | ') + ' |')
    ].join('\n');

    await navigator.clipboard.writeText(markdown);
    return true;
  } catch (error) {
    console.error('Failed to copy as markdown:', error);
    return false;
  }
};

/**
 * Copy table as CSV format
 * Useful for importing into spreadsheet tools
 */
export const copyTableAsCsv = async (tableElement) => {
  try {
    const csv = tableToCsv(tableElement);
    await navigator.clipboard.writeText(csv);
    return true;
  } catch (error) {
    console.error('Failed to copy as CSV:', error);
    return false;
  }
};

/**
 * Convert table DOM element to CSV string
 * Helper function used by other copy methods
 */
const tableToCsv = (tableElement) => {
  const rows = [];
  
  tableElement.querySelectorAll('tr').forEach(row => {
    const cells = [];
    row.querySelectorAll('th, td').forEach(cell => {
      const text = cell.textContent.trim();
      // CSV escape: wrap in quotes and escape internal quotes
      cells.push(`"${text.replace(/"/g, '""')}"`);
    });
    rows.push(cells.join(','));
  });

  return rows.join('\n');
};

/**
 * Main copy function that handles format selection
 */
export const copyTable = async (tableElement, format = 'rich') => {
  switch (format) {
    case 'rich':
      return copyTableAsRichText(tableElement);
    case 'markdown':
      return copyTableAsMarkdown(tableElement);
    case 'csv':
      return copyTableAsCsv(tableElement);
    default:
      console.error('Unknown copy format:', format);
      return copyTableAsRichText(tableElement);
  }
};
